import { useEffect, useRef, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { CalendarDays, MapPin, Search, Star, Users } from "lucide-react";
import axiosInstance from "../api/Axiosinstance";
import Testimonials from "../components/Testimonials";
import { ENDPOINTS } from "../api/Endpoints";
import { useNavigate } from "react-router-dom";
import FeaturedDestinationCarousel from "../components/FeaturedDestinationCarousel";

const destinations = [
  {
    title: "Maiduguri",
    subtitle: "Landmark, great meals and rich culture.",
    location: "Featured Destination in Nigeria",
    image: "/images/maiduguri.jpg",
    slug: "maiduguri",
  },
  {
    title: "Lagos",
    subtitle: "Beaches, nightlife and city energy.",
    location: "Featured Destination in Nigeria",
    image: "/images/lagos.jpg",
    slug: "lagos",
  },
  {
    title: "Calabar",
    subtitle: "Culture and nature escapes.",
    location: "Featured Destination in Nigeria",
    image: "/images/calabar.jpg",
    slug: "calabar",
  },
];

const placeholderStay = {
  title: "House 40 Residence",
  location: "Estate 42, Lokogoma, Abuja",
  details: "2 Beds, 2 Baths",
  price: "N80,000/night",
  rating: "4.6",
  image: "/images/house1.jpg",
};

const trendingApartments = Array.from({ length: 6 }, (_, i) => ({
  id: `trend-${i}`,
  ...placeholderStay,
}));

const summerDeals = Array.from({ length: 3 }, (_, i) => ({
  id: `summer-${i}`,
  ...placeholderStay,
}));

const popularVisits = Array.from({ length: 6 }, (_, i) => ({
  id: `popular-${i}`,
  ...placeholderStay,
}));

function StayCard({ stay }) {
  const navigate = useNavigate();
  return (
    <article className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm p-[6px]">
      <img
        src={stay.image}
        alt={stay.title}
        className="w-full h-90 object-cover rounded-lg"
      />

      <div className="pt-3 space-y-2">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1 w-full">
            <p className="text-md font-semibold text-black">{stay.title}</p>
            <div className="flex items-center gap-1 text-[10px] font-medium text-gray-600">
              <MapPin className="w-4 h-4 text-gray-600" />
              <span>{stay.location}</span>
            </div>
            <div className="flex items-center gap-1 font-medium">
              <img src="/icons/bed-double.svg" alt="" className="w-4 h-4" />
              <p className="text-xs text-gray-600">{stay.details}</p>
            </div>
          </div>

          <div className="flex items-center gap-1 text-sm font-medium text-black">
            <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
            <span>{stay.rating}</span>
          </div>
        </div>

        <div className="flex items-center justify-between w-full">
          <p className="text-xs font-medium text-gray-800">{stay.price}</p>
          <button
            className="text-xs font-medium text-[#570033] underline underline-offset-4"
            type="button"
            onClick={() => navigate(`/stays/${stay.id}`, { state: stay })}
          >
            View
          </button>
        </div>
      </div>
    </article>
  );
}

function HomeStays() {
  const [stays, setStays] = useState([]);
  const [travellersCount, setTravellersCount] = useState("1");
  const trendingCarouselRef = useRef(null);
  const summerCarouselRef = useRef(null);
  const popularCarouselRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchStays() {
      try {
        const res = await axiosInstance.get(ENDPOINTS.TOURS.STAYS);
        if (res.data.success && res.data.data?.results) {
          setStays(res.data.data.results);
        }
      } catch (error) {
        console.error("Error fetching stays:", error);
      }
    }
    fetchStays();
  }, []);

  const mapStayToCard = (stay, indexPrefix) => ({
    id: stay.id || `${indexPrefix}-${stay.title}-${stay.price}`,
    title: stay.title || placeholderStay.title,
    location: stay.locationSummary || placeholderStay.location,
    details: stay.propertySummary || placeholderStay.details,
    price: stay.formattedPrice || placeholderStay.price,
    rating: stay.rating || placeholderStay.rating,
    image: stay.images?.[0]?.url || placeholderStay.image,
  });

  const fetchedCards = stays.map((stay, index) =>
    mapStayToCard(stay, `stay-${index}`)
  );

  const trendingData =
    fetchedCards.length >= 6 ? fetchedCards.slice(0, 6) : trendingApartments;
  const summerData =
    fetchedCards.length >= 3 ? fetchedCards.slice(0, 3) : summerDeals;
  const popularData =
    fetchedCards.length >= 6 ? fetchedCards.slice(0, 6) : popularVisits;

  return (
    <div className=" min-h-screen">
      <Header />

      <main className="pb-16 max-w-[90%] lg:max-w-[80%] mx-auto">
        <section className="w-full">
          {/* Mobile: standalone form card */}
          <div className="sm:hidden max-w-xl mx-auto rounded-2xl  p-5 space-y-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm text-gray-800 font-semibold">
                Destination
              </label>
              <input
                type="text"
                placeholder="Where are you going to?"
                className="w-full rounded-xl border border-[#b8c1d0] px-4 py-3 text-base text-gray-800 outline-none focus:border-[#570033] focus:ring-2 focus:ring-[#570033]/20 placeholder:text-gray-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-sm text-gray-800 font-semibold">
                  Arrival
                </label>
                <div className="flex items-center gap-2 rounded-xl border border-[#b8c1d0] px-4 py-3 text-base text-gray-800 focus-within:border-[#570033] focus-within:ring-2 focus-within:ring-[#570033]/20">
                  <CalendarDays className="w-5 h-5 text-gray-500" />
                  <input
                    type="date"
                    className="w-full outline-none text-base text-gray-800"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm text-gray-800 font-semibold">
                  Departure
                </label>
                <div className="flex items-center gap-2 rounded-xl border border-[#b8c1d0] px-4 py-3 text-base text-gray-800 focus-within:border-[#570033] focus-within:ring-2 focus-within:ring-[#570033]/20">
                  <CalendarDays className="w-5 h-5 text-gray-500" />
                  <input
                    type="date"
                    className="w-full outline-none text-base text-gray-800"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm text-gray-800 font-semibold">
                No. of Travellers
              </label>
              <div className="flex items-center gap-2 rounded-xl border border-[#b8c1d0] px-4 py-3 text-base text-gray-800 focus-within:border-[#570033] focus-within:ring-2 focus-within:ring-[#570033]/20">
                <Users className="w-5 h-5 text-gray-500" />
                <input
                  list="home-stays-travellers"
                  value={travellersCount}
                  onChange={(e) => setTravellersCount(e.target.value)}
                  className="w-full bg-transparent outline-none text-base text-gray-800"
                  placeholder="Select"
                />
                <datalist id="home-stays-travellers">
                  {[1, 2, 3, 4, 5].map((count) => (
                    <option key={count} value={String(count)} />
                  ))}
                </datalist>
              </div>
            </div>

            <button className="w-full bg-[#570033] text-white rounded-xl px-4 py-3 flex items-center justify-center gap-2 text-base font-semibold hover:bg-[#6f0044] transition-colors">
              Search
              <Search className="w-4 h-4" />
            </button>
          </div>

          {/* Tablet/Desktop: retain hero image with overlay form */}
          <div className="hidden sm:block relative overflow-hidden rounded-xl shadow-lg bg-white">
            <img
              src="/images/stay-hero.jpg"
              alt="Stay search"
              className="w-full h-[340px] md:h-[430px] object-cover"
            />

            <div className="absolute left-4 right-4 md:left-6 md:right-6 bottom-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 bg-white/95 backdrop-blur-sm p-4 md:p-5 rounded-lg border border-gray-200 shadow-xl">
                <div className="flex flex-col gap-2">
                  <span className="text-xs text-gray-500">Destination</span>
                  <input
                    type="text"
                    placeholder="Where are you going to?"
                    className="w-full rounded border border-gray-200 px-3 py-2 text-sm text-gray-700 outline-none focus:border-[#570033]"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <span className="text-xs text-gray-500">Arrival</span>
                  <div className="flex items-center gap-2 rounded border border-gray-200 px-3 py-2 text-sm text-gray-700">
                    <CalendarDays className="w-4 h-4 text-gray-500" />
                    <input
                      type="date"
                      className="w-full outline-none text-sm"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <span className="text-xs text-gray-500">Departure</span>
                  <div className="flex items-center gap-2 rounded border border-gray-200 px-3 py-2 text-sm text-gray-700">
                    <CalendarDays className="w-4 h-4 text-gray-500" />
                    <input
                      type="date"
                      className="w-full outline-none text-sm"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <span className="text-xs text-gray-500">Guests</span>
                  <div className="flex items-center gap-2 rounded border border-gray-200 px-3 py-2 text-sm text-gray-700">
                    <Users className="w-4 h-4 text-gray-500" />
                    <input
                      type="text"
                      placeholder="Who are we expecting?"
                      className="w-full outline-none text-sm"
                    />
                  </div>
                </div>

                <div className="flex flex-col justify-end">
                  <button className="w-full bg-[#570033] text-white rounded px-4 py-3 flex items-center justify-center gap-2 text-sm font-semibold">
                    Search
                    <Search className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="h-16" />

        <section className="w-full space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-[#61013b] text-xl font-bold">
              Trending Apartments
            </h2>
            <button className="text-sm font-medium hover:text-gray-600 text-[#570033]">
              See more
            </button>
          </div>

          <div
            ref={trendingCarouselRef}
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-8 sm:overflow-visible [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {trendingData.map((stay) => (
              <div
                key={stay.id}
                className="flex-shrink-0 w-[85%] sm:w-auto sm:flex-shrink snap-center"
              >
                <StayCard stay={stay} />
              </div>
            ))}
          </div>
        </section>

        <section className="w-full mt-12">
          <FeaturedDestinationCarousel
            title="Featured Destinations"
            subtitle="Explore Top Cities with Local Experts."
            items={destinations.map((d) => ({
              title: d.title,
              subtitle: d.subtitle,
              location: d.location,
              src: d.image,
              slug: d.slug,
            }))}
            onItemClick={(item) =>
              navigate(`/explore/${item.slug || ""}`, { state: item })
            }
          />
        </section>

        <section className="w-full mt-12 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-[#61013b] text-xl font-bold">Summer Deals</h2>
            <button className="text-sm font-medium hover:text-gray-600 text-[#570033]">
              See more
            </button>
          </div>

          <div
            ref={summerCarouselRef}
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-6 sm:overflow-visible [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {summerData.map((stay) => (
              <div
                key={stay.id}
                className="flex-shrink-0 w-[85%] sm:w-auto sm:flex-shrink snap-center"
              >
                <StayCard stay={stay} />
              </div>
            ))}
          </div>
        </section>

        <section className="w-full mt-12 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-[#61013b] text-xl font-bold">Popular Visits</h2>
            <button className="text-sm font-medium hover:text-gray-600 text-[#570033]">
              See more
            </button>
          </div>

          <div
            ref={popularCarouselRef}
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory sm:grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 sm:gap-6 sm:overflow-visible [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {popularData.map((stay) => (
              <div
                key={stay.id}
                className="flex-shrink-0 w-[85%] sm:w-auto sm:flex-shrink snap-center"
              >
                <StayCard stay={stay} />
              </div>
            ))}
          </div>
        </section>

        <Testimonials className="mt-12" />
      </main>

      <Footer />
    </div>
  );
}

export default HomeStays;
